import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  TrendingUp,
  Users,
  Megaphone,
  ChevronRight,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Sparkles,
  RefreshCw,
  Globe2,
  MessagesSquare,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LeadDetailModal } from "./MarketingAnalyticsPage";
import { crmUrl } from "../lib/crmUrl";

// =============================================================================
// Sales scoring page — focused on `sales.grade` + `sales.bottleneck` per lead.
// Companion to /marketing-analytics (Marketing lead quality) and /people
// (per-manager profile cards). Separation of concerns:
//   - /marketing-analytics → which campaigns deliver qualified leads
//   - /people              → how good are individual managers overall
//   - /marketing-analytics/sales (this page) → which deals are mishandled
//                                              and where the coaching gap is
//
// Reads from existing /marketing-analytics/quality-leaderboard. No LLM calls
// triggered here — analysis runs from /marketing-analytics's bulk buttons.
// Click on a row opens the same LeadDetailModal with full AI breakdown.
// =============================================================================

function calendarMonthRange(anchorMonthsAgo = 0) {
  const now = new Date();
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - anchorMonthsAgo, 1));
  const first = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), 1));
  let last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
  if (anchorMonthsAgo === 0 && last > now) last = new Date(now);
  const label = first.toLocaleString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10), label };
}

// Grade visual + label maps. Mirror SALES_GRADE_META in MarketingAnalyticsPage.
const SALES_GRADE_PALETTE = {
  Excellent:      { cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", short: "Excellent" },
  Good:           { cls: "bg-cyan-500/15 border-cyan-500/40 text-cyan-200",          short: "Good" },
  Average:        { cls: "bg-amber-500/15 border-amber-500/40 text-amber-200",       short: "Average" },
  Weak:           { cls: "bg-orange-500/15 border-orange-500/40 text-orange-200",    short: "Weak" },
  Bad:            { cls: "bg-red-500/15 border-red-500/40 text-red-200",             short: "Bad" },
  "Not applicable": { cls: "bg-zinc-800 border-zinc-700 text-zinc-400",              short: "N/A" },
};

const ALL_SALES_GRADES = ["Excellent", "Good", "Average", "Weak", "Bad", "Not applicable"];

// Score color — big number coloured red ≤49, amber 50-69, emerald 70+.
function scoreColor(n) {
  if (n == null) return "text-zinc-500";
  if (n >= 70) return "text-emerald-300";
  if (n >= 50) return "text-amber-300";
  return "text-red-300";
}

function cleanDealName(name) {
  if (!name) return "(unnamed)";
  const cleaned = String(name)
    .replace(/^Landing\s+Lead\s*[—–\-]*\s*/i, "")
    .replace(/\[[^\]]+\]/g, "")
    .trim();
  return cleaned || String(name);
}

export default function MarketingSalesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Date range: URL params (from /to) → default to last month.
  const initialMonth = 1;
  const initial = useMemo(() => {
    const r = calendarMonthRange(initialMonth);
    return {
      from: searchParams.get("from") || r.from,
      to: searchParams.get("to") || r.to,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [monthAnchor, setMonthAnchor] = useState(initialMonth);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [gradeFilter, setGradeFilter] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState(null);
  const [sortKey, setSortKey] = useState("score_asc"); // worst-first by default
  const [openLead, setOpenLead] = useState(null);


  const fetchData = async () => {
    setLoading(true); setError("");
    try {
      // limit_top/limit_bottom default to 10 each (backend caps at 50);
      // `all` field returns every lead regardless of those slices, which
      // is what we need for the full sortable/filterable table here.
      const res = await api.get("/marketing-analytics/quality-leaderboard", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setSearchParams({ from: dateFrom, to: dateTo }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // Filter + sort the leads list.
  const filteredLeads = useMemo(() => {
    const all = data?.all || [];
    let view = all.filter((l) => {
      // Only show leads that have a sales grade (skip "no AI summary yet").
      const grade = l?.sales?.grade;
      return grade != null;
    });
    if (gradeFilter) {
      view = view.filter((l) => (l.sales?.grade) === gradeFilter);
    }
    if (ownerFilter) {
      view = view.filter((l) => (l.owner?.name || "") === ownerFilter);
    }
    const score = (l) => l.sales?.score ?? -1;
    const date = (l) => l.contact_created_at || l.deal_created_at || "";
    const amount = (l) => l.amount_usd ?? 0;
    const ownerName = (l) => l.owner?.name || "";
    const sortFn = {
      score_asc:  (a, b) => score(a) - score(b),   // worst first (where coaching is needed)
      score_desc: (a, b) => score(b) - score(a),
      date_desc:  (a, b) => date(b).localeCompare(date(a)),
      date_asc:   (a, b) => date(a).localeCompare(date(b)),
      amount_desc:(a, b) => amount(b) - amount(a),
      mgr_asc:    (a, b) => ownerName(a).localeCompare(ownerName(b)),
    }[sortKey] || ((a, b) => 0);
    return [...view].sort(sortFn);
  }, [data, gradeFilter, ownerFilter, sortKey]);

  // Per-grade count for the filter strip.
  const gradeCounts = useMemo(() => {
    const counts = {};
    (data?.all || []).forEach((l) => {
      const g = l?.sales?.grade;
      if (g) counts[g] = (counts[g] || 0) + 1;
    });
    return counts;
  }, [data]);

  // Per-manager rollup table.
  const managerRows = useMemo(() => {
    const byOwner = {};
    (data?.all || []).forEach((l) => {
      const name = l.owner?.name || "Unassigned";
      const id = l.owner?.id || "unassigned";
      const score = l.sales?.score;
      if (!byOwner[name]) {
        byOwner[name] = {
          id, name,
          count: 0, scored: 0, total_score: 0,
          grades: {},
          weak_or_bad: 0,
        };
      }
      const row = byOwner[name];
      row.count += 1;
      const g = l.sales?.grade;
      if (g) row.grades[g] = (row.grades[g] || 0) + 1;
      if (score != null) {
        row.scored += 1;
        row.total_score += score;
      }
      if (g === "Weak" || g === "Bad") row.weak_or_bad += 1;
    });
    return Object.values(byOwner)
      .map((r) => ({
        ...r,
        avg: r.scored > 0 ? Math.round(r.total_score / r.scored) : null,
        weak_pct: r.count > 0 ? Math.round((r.weak_or_bad / r.count) * 100) : 0,
      }))
      .sort((a, b) => (a.avg ?? 999) - (b.avg ?? 999)); // worst manager first
  }, [data]);

  const totalLeadsInRange = data?.total ?? 0;
  const totalGraded = (data?.all || []).filter((l) => l.sales?.grade).length;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Tab nav — mirror MarketingAnalyticsPage and MarketingPeoplePage */}
      <div className="flex items-center gap-1 border-b border-zinc-800 pb-0.5">
        <Link to="/marketing-analytics" className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition">Overview</Link>
        <Link to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> People
        </Link>
        <Link to={`/marketing-analytics/sales?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-blue-400 font-medium border-b-2 border-blue-500 -mb-0.5 inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Sales scoring
        </Link>
        <Link to={`/marketing-analytics/landings?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <Globe2 className="w-3 h-3" /> Landings &amp; Leads
        </Link>
        <Link to={`/marketing-analytics/chats?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <MessagesSquare className="w-3 h-3" /> Chats
        </Link>
      </div>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-400" /> Sales scoring
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Per-deal sales-handling grade (independent of lead quality). Worst-graded leads on top — that's where coaching pays off.
            Manager profiles on <Link to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`} className="text-blue-300 hover:underline">People</Link>.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          className="border-zinc-700 hover:bg-zinc-800 h-8"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Period picker — same UX as the other tabs */}
      <div className="flex items-center gap-2 flex-wrap text-xs bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
        <span className="text-zinc-500 uppercase tracking-wider">Period</span>
        {[{ months: 0, label: "This month" }, { months: 1, label: "Last month" }].map((p) => {
          const r = calendarMonthRange(p.months);
          const active = monthAnchor === p.months;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => { setMonthAnchor(p.months); setDateFrom(r.from); setDateTo(r.to); }}
              className={`px-2.5 py-1 rounded border transition ${
                active ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <div className="inline-flex items-center bg-zinc-900/60 border border-zinc-800 rounded">
          <button type="button"
            onClick={() => {
              const next = (monthAnchor == null ? 1 : monthAnchor) + 1;
              const r = calendarMonthRange(next);
              setMonthAnchor(next); setDateFrom(r.from); setDateTo(r.to);
            }}
            className="px-2 py-1 text-zinc-400 hover:text-zinc-100 border-r border-zinc-800"
          >
            <ChevronRight className="w-3 h-3 rotate-180" />
          </button>
          <span className="px-2 py-1 text-zinc-300 min-w-[110px] text-center">
            {monthAnchor != null && monthAnchor >= 0 ? calendarMonthRange(monthAnchor).label : "Custom"}
          </span>
          <button type="button"
            onClick={() => {
              const next = Math.max(0, (monthAnchor == null ? 1 : monthAnchor) - 1);
              const r = calendarMonthRange(next);
              setMonthAnchor(next); setDateFrom(r.from); setDateTo(r.to);
            }}
            disabled={monthAnchor === 0}
            className="px-2 py-1 text-zinc-400 hover:text-zinc-100 disabled:opacity-30 border-l border-zinc-800"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-zinc-400">
          <Input type="date" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setMonthAnchor(null); }}
            className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
          />
          <span className="text-zinc-600">→</span>
          <Input type="date" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setMonthAnchor(null); }}
            className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
          />
        </div>
      </div>

      {error && (
        <Card className="bg-red-900/20 border-red-800">
          <CardContent className="py-3 text-red-200 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-8 text-center text-zinc-400 text-sm inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading leads…
          </CardContent>
        </Card>
      )}

      {data && totalGraded === 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-8 text-center text-zinc-500 text-sm">
            No leads with a sales grade in this period yet. Run <Link to="/marketing-analytics" className="text-blue-300 hover:underline">Analyze new</Link> on the Overview tab to generate scores.
          </CardContent>
        </Card>
      )}

      {data && totalGraded > 0 && (
        <>
          {/* Per-manager rollup table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-zinc-50 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" /> Manager rollup
                <span className="ml-2 text-xs text-zinc-500 font-normal">click row to filter leads below</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-medium">Manager</th>
                    <th className="text-right px-2 py-1.5 font-medium">Leads</th>
                    <th className="text-right px-2 py-1.5 font-medium">Avg sales score</th>
                    <th className="text-right px-2 py-1.5 font-medium">Weak / Bad</th>
                    <th className="text-left px-2 py-1.5 font-medium">Grade distribution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {managerRows.map((r) => {
                    const isActive = ownerFilter === r.name;
                    return (
                      <tr
                        key={r.id}
                        onClick={() => setOwnerFilter(isActive ? null : r.name)}
                        className={`cursor-pointer transition ${isActive ? "bg-blue-500/10" : "hover:bg-zinc-900/40"}`}
                      >
                        <td className="px-2 py-2 text-zinc-100 font-medium">
                          {r.name}
                          {isActive && (
                            <span className="ml-2 text-[10px] text-blue-300 uppercase tracking-wider">filtering</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums text-zinc-300">{r.count}</td>
                        <td className={`px-2 py-2 text-right tabular-nums font-semibold ${scoreColor(r.avg)}`}>
                          {r.avg != null ? r.avg : "—"}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          <span className={r.weak_pct >= 40 ? "text-red-300" : r.weak_pct >= 20 ? "text-amber-300" : "text-zinc-400"}>
                            {r.weak_or_bad} ({r.weak_pct}%)
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex flex-wrap gap-1">
                            {ALL_SALES_GRADES.map((g) => {
                              const n = r.grades[g] || 0;
                              if (n === 0) return null;
                              const meta = SALES_GRADE_PALETTE[g];
                              return (
                                <span key={g} className={`inline-flex items-center gap-0.5 px-1 py-0 text-[10px] rounded border ${meta.cls}`}>
                                  <span className="font-semibold">{n}</span>
                                  <span className="opacity-80">{meta.short}</span>
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Grade filter strip */}
          <div className="flex items-center gap-1.5 flex-wrap text-xs">
            <button
              type="button"
              onClick={() => setGradeFilter(null)}
              className={`px-2 py-0.5 rounded border ${
                gradeFilter == null
                  ? "bg-blue-500/20 text-blue-200 border-blue-500/40"
                  : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700"
              }`}
            >
              All <span className="opacity-60">×{totalGraded}</span>
            </button>
            {ALL_SALES_GRADES.map((g) => {
              const n = gradeCounts[g] || 0;
              if (n === 0) return null;
              const meta = SALES_GRADE_PALETTE[g];
              const active = gradeFilter === g;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGradeFilter(active ? null : g)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${
                    active ? `${meta.cls} ring-1` : meta.cls + " opacity-70 hover:opacity-100"
                  }`}
                >
                  <span className="font-semibold tabular-nums">{n}</span>
                  <span>{g}</span>
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2 text-zinc-400">
              <span className="uppercase tracking-wider text-[10px]">Sort</span>
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="bg-zinc-900/60 border border-zinc-800 rounded px-2 py-0.5 text-xs"
              >
                <option value="score_asc">Worst score first</option>
                <option value="score_desc">Best score first</option>
                <option value="date_desc">Newest first</option>
                <option value="date_asc">Oldest first</option>
                <option value="amount_desc">Highest amount</option>
                <option value="mgr_asc">Manager A→Z</option>
              </select>
              {ownerFilter && (
                <button
                  type="button"
                  onClick={() => setOwnerFilter(null)}
                  className="px-2 py-0.5 rounded border bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                >
                  Clear manager filter
                </button>
              )}
            </div>
          </div>

          {/* Lead table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Lead</th>
                    <th className="text-center px-2 py-2 font-medium">Sales grade</th>
                    <th className="text-right px-2 py-2 font-medium">Score</th>
                    <th className="text-left px-2 py-2 font-medium">Bottleneck</th>
                    <th className="text-right px-2 py-2 font-medium">Amount</th>
                    <th className="text-left px-2 py-2 font-medium">Manager</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {filteredLeads.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-zinc-500 italic">
                        No leads match the current filter.
                      </td>
                    </tr>
                  )}
                  {filteredLeads.map((l) => {
                    const grade = l.sales?.grade;
                    const meta = SALES_GRADE_PALETTE[grade] || SALES_GRADE_PALETTE["Not applicable"];
                    const score = l.sales?.score;
                    const bottleneck = l.sales?.bottleneck || "";
                    const name = cleanDealName(l.deal_name);
                    const dealUrl = l.deal_crm_id
                      ? crmUrl("deal", l.deal_crm_id)
                      : null;
                    const openModal = () => setOpenLead(l);
                    return (
                      <tr
                        key={l.deal_id}
                        onClick={openModal}
                        className="hover:bg-zinc-900/40 cursor-pointer align-top"
                        title="Click to open full AI coaching breakdown"
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openModal(); }}
                              className="text-zinc-100 hover:text-blue-300 inline-flex items-center gap-1"
                            >
                              <span className="truncate max-w-[280px]">{name}</span>
                              <Sparkles className="w-3 h-3 opacity-40 shrink-0" />
                            </button>
                            {dealUrl && (
                              <a
                                href={dealUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-zinc-500 hover:text-blue-300"
                                title="Open in the CRM"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-0.5 inline-flex items-center gap-1">
                            <Megaphone className="w-2.5 h-2.5" />
                            <span className="truncate max-w-[300px]">{l.campaign || "Untagged"}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] ${meta.cls}`}>
                            {grade}
                          </span>
                        </td>
                        <td className={`px-2 py-2 text-right tabular-nums font-semibold ${scoreColor(score)}`}>
                          {score != null ? score : "—"}
                        </td>
                        <td className="px-2 py-2 text-zinc-300 max-w-[360px] leading-snug">
                          {bottleneck || <span className="italic text-zinc-600">—</span>}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                          {l.amount_usd > 0 ? (
                            <span className="text-emerald-300 font-mono">
                              ${l.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-zinc-300 whitespace-nowrap">{l.owner?.name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <div className="text-[11px] text-zinc-500">
            {filteredLeads.length} of {totalGraded} graded leads ({totalLeadsInRange} total in range).{" "}
            To re-grade, go to <Link to="/marketing-analytics" className="text-blue-300 hover:underline">Overview</Link>.
          </div>
        </>
      )}

      {openLead && (
        <LeadDetailModal lead={openLead} onClose={() => setOpenLead(null)} />
      )}
    </div>
  );
}
